import { useState, useEffect } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { 
  Target, 
  TrendingUp, 
  DollarSign, 
  Percent,
  CheckCircle,
  AlertTriangle,
  RefreshCw,
  Calculator
} from "lucide-react";
import { 
  LineChart, 
  Line, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  ReferenceLine,
  Area,
  ComposedChart,
  Legend
} from "recharts";

const formatCurrency = (value) => {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0
  }).format(value);
};

const BreakEven = () => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(true);
  const [analysis, setAnalysis] = useState(null);
  const [simulating, setSimulating] = useState(false);
  const [simParams, setSimParams] = useState({
    fixed_costs: "",
    variable_cost_ratio: 60,
    target_profit: 0
  });
  const [simResult, setSimResult] = useState(null);

  useEffect(() => {
    fetchAnalysis();
  }, []);

  const fetchAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/breakeven`, getAuthHeaders());
      setAnalysis(response.data);
      setSimParams(prev => ({
        ...prev,
        fixed_costs: response.data.fixed_costs.toString(),
        variable_cost_ratio: response.data.variable_cost_ratio
      }));
    } catch (error) {
      toast.error("Errore nel caricamento analisi");
    } finally {
      setLoading(false);
    }
  };

  const runSimulation = async () => {
    setSimulating(true);
    try {
      const response = await axios.post(
        `${API}/breakeven/simulate`,
        {
          fixed_costs: parseFloat(simParams.fixed_costs) || null,
          variable_cost_ratio: simParams.variable_cost_ratio,
          target_profit: simParams.target_profit
        },
        getAuthHeaders()
      );
      setSimResult(response.data);
      toast.success("Simulazione completata");
    } catch (error) {
      toast.error("Errore nella simulazione");
    } finally {
      setSimulating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="breakeven-page">
      <div className="page-header">
        <h1 className="page-title">Break Even Point</h1>
        <p className="page-subtitle">Analisi del punto di pareggio</p>
      </div>

      {/* Status Banner */}
      {analysis && (
        <Card className={`mb-6 rounded-2xl border-0 ${
          analysis.is_profitable 
            ? 'bg-[#34C759]/5' 
            : 'bg-[#FF3B30]/5'
        }`}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                analysis.is_profitable ? 'bg-[#34C759]/10' : 'bg-[#FF3B30]/10'
              }`}>
                {analysis.is_profitable ? (
                  <CheckCircle className="w-6 h-6 text-[#34C759]" />
                ) : (
                  <AlertTriangle className="w-6 h-6 text-[#FF3B30]" />
                )}
              </div>
              <div>
                <p className={`font-semibold ${
                  analysis.is_profitable ? 'text-[#34C759]' : 'text-[#FF3B30]'
                }`}>
                  {analysis.is_profitable 
                    ? 'Sopra il Break Even Point' 
                    : 'Sotto il Break Even Point'}
                </p>
                <p className="text-[#86868B]">
                  {analysis.is_profitable 
                    ? `Margine di sicurezza: ${formatCurrency(analysis.margin_of_safety)} (${analysis.margin_of_safety_percent.toFixed(1)}%)`
                    : `Gap da colmare: ${formatCurrency(Math.abs(analysis.margin_of_safety))}`
                  }
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <Card className="stat-card card-hover" data-testid="breakeven-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-card-label">Break Even Point</p>
                <p className="stat-card-value text-2xl">{formatCurrency(analysis?.breakeven_revenue || 0)}</p>
                <p className="text-sm text-[#86868B] mt-1">Ricavi necessari</p>
              </div>
              <div className="w-12 h-12 bg-[#007AFF]/10 rounded-2xl flex items-center justify-center">
                <Target className="w-6 h-6 text-[#007AFF]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card card-hover" data-testid="current-revenue-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-card-label">Ricavi Attuali</p>
                <p className="stat-card-value text-2xl">{formatCurrency(analysis?.current_revenue || 0)}</p>
                <p className="text-sm text-[#86868B] mt-1">Totale entrate</p>
              </div>
              <div className="w-12 h-12 bg-[#34C759]/10 rounded-2xl flex items-center justify-center">
                <TrendingUp className="w-6 h-6 text-[#34C759]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card card-hover" data-testid="fixed-costs-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-card-label">Costi Fissi</p>
                <p className="stat-card-value text-2xl">{formatCurrency(analysis?.fixed_costs || 0)}</p>
                <p className="text-sm text-[#86868B] mt-1">Da coprire</p>
              </div>
              <div className="w-12 h-12 bg-[#FF9500]/10 rounded-2xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-[#FF9500]" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="stat-card card-hover" data-testid="contribution-margin-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-card-label">Margine Contribuzione</p>
                <p className="stat-card-value text-2xl">{(analysis?.contribution_margin || 0).toFixed(1)}%</p>
                <p className="text-sm text-[#86868B] mt-1">Per € di ricavo</p>
              </div>
              <div className="w-12 h-12 bg-[#AF52DE]/10 rounded-2xl flex items-center justify-center">
                <Percent className="w-6 h-6 text-[#AF52DE]" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Chart */}
      <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)] mb-8">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
            Grafico Break Even
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-[400px]" data-testid="breakeven-chart">
            {analysis?.projection_data && (
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={analysis.projection_data}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" />
                  <XAxis 
                    dataKey="revenue" 
                    tickFormatter={(value) => `€${(value/1000).toFixed(0)}k`}
                    stroke="#86868B"
                    fontSize={12}
                  />
                  <YAxis 
                    tickFormatter={(value) => `€${(value/1000).toFixed(0)}k`}
                    stroke="#86868B"
                    fontSize={12}
                  />
                  <Tooltip 
                    formatter={(value, name) => {
                      const labels = {
                        revenue: 'Ricavi',
                        total_costs: 'Costi Totali',
                        fixed_costs: 'Costi Fissi',
                        profit: 'Profitto'
                      };
                      return [formatCurrency(value), labels[name] || name];
                    }}
                    contentStyle={{ 
                      background: 'white', 
                      border: 'none', 
                      borderRadius: '12px',
                      boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Legend 
                    formatter={(value) => {
                      const labels = {
                        revenue: 'Ricavi',
                        total_costs: 'Costi Totali',
                        fixed_costs: 'Costi Fissi',
                        profit: 'Profitto'
                      };
                      return labels[value] || value;
                    }}
                  />
                  <ReferenceLine 
                    x={analysis.breakeven_revenue} 
                    stroke="#007AFF" 
                    strokeDasharray="5 5"
                    label={{ value: 'BEP', fill: '#007AFF', fontSize: 12 }}
                  />
                  <ReferenceLine y={0} stroke="#86868B" />
                  <Line 
                    type="monotone" 
                    dataKey="revenue" 
                    stroke="#34C759" 
                    strokeWidth={2}
                    dot={false}
                    name="revenue"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="total_costs" 
                    stroke="#FF3B30" 
                    strokeWidth={2}
                    dot={false}
                    name="total_costs"
                  />
                  <Line 
                    type="monotone" 
                    dataKey="fixed_costs" 
                    stroke="#FF9500" 
                    strokeWidth={1}
                    strokeDasharray="5 5"
                    dot={false}
                    name="fixed_costs"
                  />
                  <Area 
                    type="monotone" 
                    dataKey="profit" 
                    fill="#007AFF"
                    fillOpacity={0.1}
                    stroke="#007AFF"
                    strokeWidth={2}
                    name="profit"
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Simulator */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
          <CardHeader>
            <CardTitle className="text-lg font-semibold text-[#1D1D1F] flex items-center gap-2">
              <Calculator className="w-5 h-5 text-[#007AFF]" />
              Simulatore
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="space-y-2">
              <Label className="text-[#1D1D1F]">Costi Fissi (€)</Label>
              <Input
                type="number"
                value={simParams.fixed_costs}
                onChange={(e) => setSimParams({ ...simParams, fixed_costs: e.target.value })}
                placeholder="Inserisci costi fissi"
                className="h-12 rounded-xl border-[#D2D2D7]"
                data-testid="sim-fixed-costs"
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <Label className="text-[#1D1D1F]">Costi Variabili (%)</Label>
                <span className="font-semibold text-[#007AFF]">{simParams.variable_cost_ratio.toFixed(0)}%</span>
              </div>
              <Slider
                value={[simParams.variable_cost_ratio]}
                onValueChange={(value) => setSimParams({ ...simParams, variable_cost_ratio: value[0] })}
                min={20}
                max={90}
                step={5}
                className="[&_[role=slider]]:bg-[#007AFF] [&_[role=slider]]:border-0 [&_[role=slider]]:w-5 [&_[role=slider]]:h-5"
                data-testid="sim-variable-ratio"
              />
              <div className="flex justify-between text-xs text-[#86868B]">
                <span>20%</span>
                <span>90%</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-[#1D1D1F]">Profitto Target (€)</Label>
              <Input
                type="number"
                value={simParams.target_profit}
                onChange={(e) => setSimParams({ ...simParams, target_profit: parseFloat(e.target.value) || 0 })}
                placeholder="0 per break-even"
                className="h-12 rounded-xl border-[#D2D2D7]"
                data-testid="sim-target-profit"
              />
            </div>

            <Button
              onClick={runSimulation}
              disabled={simulating}
              className="w-full h-12 bg-[#007AFF] hover:bg-[#0071E3] text-white rounded-full"
              data-testid="run-sim-btn"
            >
              {simulating ? (
                <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              ) : null}
              Calcola
            </Button>
          </CardContent>
        </Card>

        {/* Simulation Result */}
        {simResult && (
          <Card className="lg:col-span-2 bg-white rounded-2xl border-0 shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
            <CardHeader>
              <CardTitle className="text-lg font-semibold text-[#1D1D1F]">
                Risultato Simulazione
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="p-4 bg-[#F5F5F7] rounded-xl">
                  <p className="text-sm text-[#86868B]">Break Even Simulato</p>
                  <p className="text-2xl font-semibold text-[#1D1D1F]">
                    {formatCurrency(simResult.breakeven_revenue)}
                  </p>
                </div>
                <div className="p-4 bg-[#F5F5F7] rounded-xl">
                  <p className="text-sm text-[#86868B]">Gap da Ricavi Attuali</p>
                  <p className={`text-2xl font-semibold ${
                    simResult.gap >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'
                  }`}>
                    {simResult.gap >= 0 ? '+' : ''}{formatCurrency(simResult.gap)}
                  </p>
                </div>
              </div>

              <h4 className="font-medium text-[#1D1D1F] mb-4">Scenari Costi Variabili</h4>
              <div className="space-y-3">
                {simResult.scenarios?.map((scenario, i) => (
                  <div 
                    key={i} 
                    className={`flex items-center justify-between p-3 rounded-xl ${
                      scenario.variable_cost_ratio === simParams.variable_cost_ratio 
                        ? 'bg-[#007AFF]/10 border border-[#007AFF]/20' 
                        : 'bg-[#F5F5F7]'
                    }`}
                  >
                    <div>
                      <span className="font-medium text-[#1D1D1F]">
                        {scenario.variable_cost_ratio.toFixed(0)}% costi var.
                      </span>
                      <span className="text-[#86868B] ml-2">
                        (margine {scenario.contribution_margin.toFixed(0)}%)
                      </span>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-[#1D1D1F]">
                        {formatCurrency(scenario.breakeven_revenue)}
                      </p>
                      <p className={`text-sm ${
                        scenario.gap_from_current >= 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'
                      }`}>
                        {scenario.gap_from_current >= 0 ? '+' : ''}{formatCurrency(scenario.gap_from_current)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default BreakEven;
